import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle2, Quote } from "lucide-react";
import { SITE, WHY_TRJ } from "@/lib/site";
import PageHero, { ScheduleConsultationButton } from "@/components/site/PageHero";

const WHO_SERVED = [
  "Individuals & Families",
  "Entrepreneurs",
  "Small Businesses",
  "Growing Companies",
  "Nonprofit Organizations",
  "Churches & Ministries",
  "Professional Service Providers",
  "Community Leaders",
];

export default function About() {
  return (
    <div data-testid="about-page">
      <PageHero
        eyebrow="About TRJ Services"
        title="Building Financial Clarity, Structure, and Confidence."
        subtitle="TRJ Services was founded on the belief that individuals, businesses, nonprofits, and ministries deserve more than transactional financial services. They deserve trusted guidance, strategic support, and a partner committed to helping them build a strong financial foundation."
        image={SITE.assets.meeting}
      >
        <ScheduleConsultationButton testid="about-hero-schedule-btn" />
        <Link to="/solutions" className="trj-btn-outline-white" data-testid="about-hero-solutions-btn">
          Explore Solutions <ArrowRight className="w-4 h-4" />
        </Link>
      </PageHero>

      {/* OUR APPROACH */}
      <section id="our-story" className="bg-white py-20 md:py-28" data-testid="about-our-approach">
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-7">
            <div className="trj-eyebrow mb-4">Our Approach</div>
            <h2 className="font-serif-trj text-4xl md:text-5xl text-[var(--trj-navy)] leading-tight">More Than a Tax Return.</h2>
            <div className="trj-divider-gold mt-6" />
            <div className="mt-6 space-y-5 text-[15.5px] text-[var(--trj-muted)] leading-relaxed max-w-2xl">
              <p>
                At TRJ Services, we understand that many financial challenges are connected to larger
                organizational and operational issues. That&apos;s why we take a comprehensive approach that
                goes beyond filing forms and preparing returns.
              </p>
              <p>
                We help clients establish systems, improve financial organization, maintain compliance,
                and make informed decisions that support long-term growth. Our goal is not simply to solve
                today&apos;s problem but to help prepare you for tomorrow&apos;s opportunities.
              </p>
            </div>
          </div>
          <div className="lg:col-span-5">
            <img src={SITE.assets.team} alt="Professional team meeting" className="w-full h-[440px] object-cover" />
          </div>
        </div>
      </section>

      {/* MEET DR TINA */}
      <section id="meet-dr-tina" className="bg-[var(--trj-light)] py-20 md:py-28" data-testid="about-meet-dr-tina">
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-5 order-2 lg:order-1">
            <div className="relative">
              <img src={SITE.assets.founder} alt="Tina Jacobs" className="w-full max-w-[460px] object-cover border-b-4 border-[var(--trj-gold)]" />
            </div>
          </div>
          <div className="lg:col-span-7 order-1 lg:order-2">
            <div className="trj-eyebrow mb-4">Meet Tina Jacobs</div>
            <h2 className="font-serif-trj text-4xl md:text-5xl text-[var(--trj-navy)] leading-tight">
              &ldquo;Helping individuals, businesses, nonprofits, and ministries create financial clarity,
              structure, and confidence.&rdquo;
            </h2>
            <div className="trj-divider-gold mt-6" />
            <div className="mt-6 space-y-5 text-[15.5px] text-[var(--trj-muted)] leading-relaxed">
              <p>
                Tina Jacobs is a financial professional, business consultant, nonprofit advisor,
                ministry leader, and entrepreneur with a passion for helping individuals and organizations
                create lasting success.
              </p>
              <p>
                For more than 25 years, she has worked with families, business owners, nonprofits, and
                ministries — providing guidance in tax preparation, business development, compliance,
                organizational structure, and financial management.
              </p>
              <p>
                As the founder of multiple businesses and organizations, Tina understands the
                challenges leaders face when balancing growth, compliance, operations, and financial
                responsibilities. Her practical experience allows her to provide solutions that are both
                strategic and actionable.
              </p>
              <p>
                She believes that strong financial systems create stronger organizations, stronger families,
                and stronger communities.
              </p>
            </div>
            <div className="mt-8 p-6 bg-white border-l-4 border-[var(--trj-gold)] relative">
              <Quote className="w-6 h-6 text-[var(--trj-gold)] absolute -top-3 left-4 bg-white p-1" />
              <p className="font-serif-trj italic text-xl text-[var(--trj-navy)] leading-snug">
                &ldquo;My goal is to help individuals, businesses, nonprofits, and ministries create the
                structure, clarity, and confidence needed to build a stronger future.&rdquo;
              </p>
              <div className="mt-3 text-xs uppercase tracking-widest text-[var(--trj-green)]">— Tina Jacobs</div>
            </div>
          </div>
        </div>
      </section>

      {/* WHY CLIENTS CHOOSE */}
      <section id="why-trj" className="bg-white py-20 md:py-28" data-testid="about-why-trj">
        <div className="max-w-7xl mx-auto px-6">
          <div className="max-w-3xl mb-12">
            <div className="trj-eyebrow mb-4">Why Clients Choose TRJ Services</div>
            <h2 className="font-serif-trj text-4xl md:text-5xl text-[var(--trj-navy)] leading-tight">Experience You Can Trust.</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-gray-200 border border-gray-200">
            {WHY_TRJ.map((w) => (
              <div key={w.label} className="bg-white p-6 flex items-start gap-3">
                <CheckCircle2 className="w-5 h-5 text-[var(--trj-green)] shrink-0 mt-0.5" />
                <div className="text-sm text-[var(--trj-navy)] font-medium leading-snug">{w.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHO WE SERVE LIST */}
      <section className="bg-[var(--trj-light)] py-20" data-testid="about-who-we-serve">
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-5">
            <div className="trj-eyebrow mb-4">Who We Serve</div>
            <h2 className="font-serif-trj text-3xl md:text-4xl text-[var(--trj-navy)] leading-tight">A diverse community of leaders.</h2>
            <p className="mt-5 text-[var(--trj-muted)] leading-relaxed">
              TRJ Services proudly serves a wide range of clients — each receiving the same standard
              of professional, ethical, year-round support.
            </p>
          </div>
          <div className="lg:col-span-7">
            <ul className="grid sm:grid-cols-2 gap-3">
              {WHO_SERVED.map((it) => (
                <li key={it} className="flex items-center gap-3 bg-white border border-gray-200 px-4 py-3 text-sm text-[var(--trj-navy)]">
                  <span className="w-1.5 h-1.5 bg-[var(--trj-gold)]" /> {it}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* COMMITMENT + CTA */}
      <section className="bg-[var(--trj-navy)] py-20 md:py-28 text-white text-center relative overflow-hidden" data-testid="about-commitment">
        <div className="absolute inset-0 opacity-20">
          <img src={SITE.assets.boardroom} alt="" className="w-full h-full object-cover"/>
          <div className="absolute inset-0 bg-gradient-to-b from-[#0B1F3A] via-[#0B1F3A]/85 to-[#0B1F3A]"/>
        </div>
        <div className="relative max-w-4xl mx-auto px-6">
          <div className="trj-eyebrow mb-4">Our Commitment</div>
          <h2 className="font-serif-trj text-4xl md:text-5xl leading-tight">
            Ready to take the next step?
          </h2>
          <p className="mt-6 text-white/80 leading-relaxed">
            Schedule a consultation today and discover how TRJ Services can help you create clarity,
            strengthen your financial foundation, and achieve your goals.
          </p>
          <div className="mt-9 flex justify-center flex-wrap gap-3">
            <ScheduleConsultationButton testid="about-cta-schedule-btn" />
            <Link to="/contact" className="trj-btn-gold" data-testid="about-cta-review-btn">
              Request Financial Review
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
